import { useEffect, useRef } from 'react';
import { Box, Image } from '@chakra-ui/react';
import { QRCodeSVG } from 'qrcode.react';
import bwipjs from 'bwip-js';
import {
  getPreviewBarcodeImage,
  getPreviewBarcodeDimensions,
} from '../utils/applePassPreview';

type BarcodeType = 'qr' | 'code128' | 'pdf417' | 'aztec';

const BWIP_TYPE_MAP: Record<string, string> = {
  code128: 'code128',
  pdf417: 'pdf417compact',
  aztec: 'azteccode',
};

interface BarcodePreviewProps {
  type: BarcodeType;
  value?: string;
}

const BarcodePreview = ({ type, value }: BarcodePreviewProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { width, height } = getPreviewBarcodeDimensions(type);
  const numWidth = parseInt(width) || 100;
  const numHeight = parseInt(height) || 100;

  // For non-QR barcodes, render via bwip-js to canvas
  useEffect(() => {
    if (!canvasRef.current || !value || type === 'qr') return;
    const bwipType = BWIP_TYPE_MAP[type];
    if (!bwipType) return;

    try {
      bwipjs.toCanvas(canvasRef.current, {
        bcid: bwipType,
        text: value,
        scale: 2,
        includetext: false,
      });
    } catch {
      // If encoding fails (e.g. invalid characters), clear the canvas
      const ctx = canvasRef.current.getContext('2d');
      if (ctx)
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
  }, [type, value]);

  // No value — show static fallback image
  if (!value) {
    const fallback = getPreviewBarcodeImage(type);
    if (!fallback) return null;
    return <Image width={width} height={height} src={fallback} alt="" />;
  }

  // QR code — use qrcode.react SVG
  if (type === 'qr') {
    return (
      <Box
        width={width}
        height={height}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <QRCodeSVG value={value} size={numWidth} />
      </Box>
    );
  }

  // Code128 / PDF417 / Aztec — render to canvas
  return (
    <canvas ref={canvasRef} style={{ width, height, objectFit: 'contain' }} />
  );
};

export default BarcodePreview;
